import type { Stamp } from './hlc.ts'
import type { OpEnvelope } from './ops.ts'
import {
  readBoolean,
  readCount,
  readOpen,
  readOwner,
  readResidence,
  readSource,
  readString,
  readTripResidence,
  type Read,
} from './payloads.ts'
import { writeRegister, type Register } from './registers.ts'
import type {
  HouseholdState,
  EntryState,
  GearState,
  NoteState,
  PersonState,
  PhaseValue,
  PieceState,
  PlaceState,
  TaskState,
  TripState,
} from './state.ts'

/**
 * The fold's starting point (`sync-protocol.md` §8.4): no places, no gear, no
 * people, no trips, and nothing unfolded.
 */
export function emptyState(): HouseholdState {
  return {
    places: {},
    gear: {},
    people: {},
    trips: {},
    unfolded: { count: 0, types: {} },
  }
}

/**
 * §5.3 obligation 1: an unknown `type` is retained, not rejected. Counted
 * here so it is observable, not just silently tolerated.
 */
function noteUnfolded(state: HouseholdState, type: string): HouseholdState {
  return {
    ...state,
    unfolded: {
      count: state.unfolded.count + 1,
      types: {
        ...state.unfolded.types,
        [type]: (state.unfolded.types[type] ?? 0) + 1,
      },
    },
  }
}

/**
 * **The generic writer the other seven are now spelled in terms of**, and not
 * the one `writeEntry`'s docblock argued against for five slices.
 *
 * That argument was against a `writeEntity<K extends keyof HouseholdState>`
 * taking the **map key** — it needs the key *and* the entity type as
 * parameters, spreads back through a computed key TypeScript widens, and
 * reads worse at every call site than the six lines it replaces. All of that
 * still holds. This takes the **map itself** and returns a new one, so there
 * is no key, no computed spread and no cast: each named writer keeps its own
 * signature, its own docblock and its own place in the file, and hands this
 * the one map it owns.
 *
 * **What is shared is the identity guard, which is the part that had to
 * stop being copied.** An update that changes no register must return the
 * object it was given, so a losing LWW write cannot fabricate a new entity —
 * and through it a new `HouseholdState`, invalidating `slice.ts`'s and
 * `containment.ts`'s `WeakMap` memos with an op that changed nothing.
 * `undefined` here means exactly that: *nothing changed, return what you were
 * given*, and every caller is one line of exactly that shape.
 *
 * `fresh` is the bare entity a first write creates, passed in rather than
 * built here — `{ id }` is not assignable to a generic `T`, and a cast in the
 * reducer to save seven identical literals is the wrong trade.
 *
 * **`persistCreated` is `writeEntry`'s departure, now named.** An Entry, a
 * Note and a Piece are each created by an op that may write no register at
 * all (a malformed `trip.entry_added` still creates a bare, sourceless
 * Entry), so identity alone cannot tell *existed, untouched* from *just
 * created, untouched* apart. The four top-level entities and a Task have a
 * creating op that always writes at least one register, so the plain guard is
 * exact for them and the flag stays off.
 */
function writeEntity<T extends object>(
  map: Readonly<Record<string, T>> | undefined,
  id: string,
  fresh: T,
  stamp: Stamp,
  update: (entity: T, stamp: Stamp) => T,
  persistCreated = false,
): Record<string, T> | undefined {
  const existing = map?.[id]
  const current = existing ?? fresh
  const updated = update(current, stamp)
  if (updated === current && (existing !== undefined || !persistCreated)) {
    return undefined
  }
  return { ...map, [id]: updated }
}

/**
 * Reads or creates the `PlaceState` at `id`, applies `update`, and copies
 * only what changed: the entity, the `places` map, and the top-level state.
 * Returns `state` unchanged — the identical object — when `update` returns
 * the identical `PlaceState` it was given, which is what a lost LWW write
 * does (`writeRegister`'s contract, propagated). Task 6 reuses this shape for
 * `gear`.
 */
function writePlace(
  state: HouseholdState,
  id: string,
  stamp: Stamp,
  update: (place: PlaceState, stamp: Stamp) => PlaceState,
): HouseholdState {
  const places = writeEntity(state.places, id, { id }, stamp, update)
  return places === undefined ? state : { ...state, places }
}

/**
 * The `writePlace` shape, reused for `gear` (Task 6). Reads or creates the
 * `GearState` at `id`, applies `update`, and copies only what changed.
 */
function writeGear(
  state: HouseholdState,
  id: string,
  stamp: Stamp,
  update: (gear: GearState, stamp: Stamp) => GearState,
): HouseholdState {
  const gear = writeEntity(state.gear, id, { id }, stamp, update)
  return gear === undefined ? state : { ...state, gear }
}

/**
 * The `writePlace`/`writeGear` shape, reused for `people` (Task 7). Reads or
 * creates the `PersonState` at `id`, applies `update`, and copies only what
 * changed.
 */
function writePerson(
  state: HouseholdState,
  id: string,
  stamp: Stamp,
  update: (person: PersonState, stamp: Stamp) => PersonState,
): HouseholdState {
  const people = writeEntity(state.people, id, { id }, stamp, update)
  return people === undefined ? state : { ...state, people }
}

/**
 * The `writePlace`/`writeGear`/`writePerson` shape a **fourth** time, for
 * `trips` (S6). Reads or creates the `TripState` at `id`, applies `update`,
 * and copies only what changed.
 *
 * Four copies of a six-line function is where the generic argument started
 * (spec §2). A `writeEntity<K>` taking the **map key** is still refused, for
 * the reason recorded there and above {@link writeEntity}; what all seven
 * writers now share is the identity guard, handed the map rather than a key
 * into it, so each of these keeps the signature its call sites read.
 */
function writeTrip(
  state: HouseholdState,
  id: string,
  stamp: Stamp,
  update: (trip: TripState, stamp: Stamp) => TripState,
): HouseholdState {
  const trips = writeEntity(state.trips, id, { id }, stamp, update)
  return trips === undefined ? state : { ...state, trips }
}

/**
 * The fifth entity writer, and the first at two levels.
 *
 * Nested inside `writeTrip` so a Trip is created by an Entry op exactly as it
 * is by any other Trip op, and with the same identity check at each level: an
 * update that changes no register returns the object it was given, and the
 * `writeTrip` above it then returns the state it was given.
 *
 * This docblock said for five slices that a sixth instance should re-open the
 * generic-writer argument. The sixth and seventh landed in one week, in
 * parallel worktrees, and neither could take it; it is taken now — see
 * {@link writeEntity}, which is not the key-taking generic this file kept
 * refusing.
 *
 * One departure from the four top-level writers, and it is the `true` passed
 * below: an Entry that did **not** already exist is persisted even when
 * `update` writes no register at all — a malformed `trip.entry_added` still
 * creates a bare, sourceless Entry (`tripEntryAdded`'s doc). `writeTrip`,
 * `writeGear` et al. need no such case because their sole creating op
 * (`trip.created`, `gear.recorded`) always writes at least one register
 * unconditionally; `trip.entry_added` has no such unconditional field, so
 * identity alone cannot tell "existed, untouched" from "just created,
 * untouched" apart. An already-existing Entry that a later malformed op does
 * not change still returns `trip` unaltered, exactly like every other writer
 * here.
 */
function writeEntry(
  state: HouseholdState,
  tripId: string,
  entryId: string,
  stamp: Stamp,
  update: (entry: EntryState, stamp: Stamp) => EntryState,
): HouseholdState {
  return writeTrip(state, tripId, stamp, (trip, st) => {
    const entries = writeEntity(
      trip.entries,
      entryId,
      { id: entryId },
      st,
      update,
      true,
    )
    return entries === undefined ? trip : { ...trip, entries }
  })
}

/**
 * Nested inside {@link writeEntry} exactly as that is nested inside
 * `writeTrip` — the third level of one pattern, not a new one.
 *
 * The identity guard is the same and matters for the same reason: a losing
 * write must return the identical object so `slice.ts`'s `WeakMap` memo is
 * not invalidated by an op that changed nothing.
 */
/**
 * The Trip's second nested entity map (S12), and `writeEntry`'s twin one map
 * over rather than a new shape.
 *
 * The identity guard reads exactly as `writeEntry`'s and is there for the
 * identical reason: an update that changes no register must return the object
 * it was given, so a losing write cannot fabricate a new `trip` and invalidate
 * `slice.ts`'s `WeakMap` memo. The `existing !== undefined` half is what tells
 * "existed, untouched" from "just created, untouched" — needed here for
 * `trip.note_kept`, whose payload can be malformed enough to write nothing
 * while the Note itself is still legitimately created.
 */
function writeNote(
  state: HouseholdState,
  tripId: string,
  noteId: string,
  stamp: Stamp,
  update: (note: NoteState, stamp: Stamp) => NoteState,
): HouseholdState {
  return writeTrip(state, tripId, stamp, (trip, st) => {
    const notes = writeEntity(
      trip.notes,
      noteId,
      { id: noteId },
      st,
      update,
      true,
    )
    return notes === undefined ? trip : { ...trip, notes }
  })
}

function writePiece(
  state: HouseholdState,
  tripId: string,
  entryId: string,
  personId: string,
  stamp: Stamp,
  update: (piece: PieceState, stamp: Stamp) => PieceState,
): HouseholdState {
  return writeEntry(state, tripId, entryId, stamp, (entry, st) => {
    const pieces = writeEntity(
      entry.pieces,
      personId,
      { id: personId },
      st,
      update,
      true,
    )
    return pieces === undefined ? entry : { ...entry, pieces }
  })
}

/**
 * The **sixth** entity writer (S13, spec §1.1), for `tasks.<task_id>` — nested
 * inside `writeTrip` exactly as {@link writeEntry} is, and carrying the same
 * identity guard for the same reason: a losing write must return the object it
 * was given, or `slice.ts`'s `WeakMap` memo is invalidated by an op that
 * changed nothing.
 *
 * **`writeEntry`'s docblock said a sixth should re-open the generic-writer
 * argument, and it fired here — twice at once.** S12's `writeNote` was the
 * seventh, landing in a parallel worktree the same week, which is precisely
 * why neither slice could take the refactor: it rewrites the bodies of every
 * writer in the one file the whole correctness argument rests on, and hands
 * the other branch the collision architecture §8.6 promised these two would
 * not have. Both landed; the shared guard is {@link writeEntity}.
 *
 * Unlike `writeEntry` this needs **no** "created but untouched" departure.
 * That case exists there because `trip.entry_added` writes no register
 * unconditionally, so identity alone cannot tell *existed, untouched* from
 * *just created, untouched* apart; `trip.task_added` always writes `text`, so
 * a malformed one persists nothing and the ordinary guard is exact.
 */
function writeTask(
  state: HouseholdState,
  tripId: string,
  taskId: string,
  stamp: Stamp,
  update: (task: TaskState, stamp: Stamp) => TaskState,
): HouseholdState {
  return writeTrip(state, tripId, stamp, (trip, st) => {
    const tasks = writeEntity(trip.tasks, taskId, { id: taskId }, st, update)
    return tasks === undefined ? trip : { ...trip, tasks }
  })
}

/**
 * Writes `read`'s value into `current` via `writeRegister` only when `read`
 * actually carries a value — an absent (or malformed) field leaves `current`
 * untouched, propagating its identity so a caller can tell nothing changed.
 * This is what lets `gear.recorded` seed only the fields its payload
 * actually carries (§4.3), including falsy ones: `container: false` and
 * `owned_count: 0` are `{ kind: 'value' }`, not `{ kind: 'absent' }`.
 */
function writeIfPresent<T>(
  current: Register<T> | undefined,
  read: Read<T>,
  stamp: Stamp,
): Register<T> | undefined {
  if (read.kind !== 'value') return current
  return writeRegister(current, read.value, stamp)
}

/**
 * The `writeIfPresent` counterpart for a register whose **declared type
 * includes `null`** — named for that, not for `name`, so it generalises for
 * free the next time a nullable, non-`name` register shows up. **S6 is that
 * slice, and it needed no change here**: `trip.dates_set`'s `start`/`end`
 * were named in this comment as the anticipated case and turned out to be
 * exactly this shape, which is the whole return on naming the function for
 * the type rather than for the field. The registers on that side of the line
 * are now `PlaceState.name`, `GearState.name`, `PersonState.name`,
 * `TripState.name` and `TripState.startDate`/`endDate` — all
 * `Register<string | null>`.
 *
 * The rule, uniform across every register in this reducer: **a register
 * whose declared type includes `null` takes an explicit `null` as a clear; a
 * register whose type does not treats a `null` payload as malformed and
 * ignores it.** §1.3 is the authority for this — it states the
 * absent-versus-null distinction generally, with no per-field carve-out.
 * §5.3 obligation 5 is *not* the authority: its text runs one way only
 * (treating an *absent* field as an explicit clear), so citing it to justify
 * collapsing `null` into absent — the reverse direction — was an
 * overstatement (`sync-protocol.md` §4.3's note records the correction).
 *
 * Every op that writes a `name` register — `place.recorded`/
 * `place.renamed`, `gear.recorded`/`gear.renamed`, `person.recorded`/
 * `person.renamed`, `trip.created`/`trip.renamed` — goes through this
 * function rather than `writeIfPresent`, so all of them behave identically
 * wherever they are written. (An earlier version of this file
 * had `setPlaceName` collapse `null` into absent, reasoning from §4.1's
 * payload shape rather than from the state type; that made an explicit
 * clear fold identically to an absent field, exactly the conflation
 * obligation 5's own direction forbids. Corrected once, here, rather than
 * per op.)
 */
function writeNullableIfPresent<T>(
  current: Register<T | null> | undefined,
  read: Read<T>,
  stamp: Stamp,
): Register<T | null> | undefined {
  if (read.kind === 'absent') return current
  const value = read.kind === 'null' ? null : read.value
  return writeRegister(current, value, stamp)
}

type Handler = (
  state: HouseholdState,
  op: OpEnvelope,
  stamp: Stamp,
) => HouseholdState

/**
 * `place.recorded` and `place.renamed` both just set `name` (`sync-protocol.md`
 * §4.1) — the entity itself is created by `writePlace` either way, out of
 * authoring order or not (§8.2).
 */
const setPlaceName: Handler = (state, op, stamp) =>
  writePlace(state, op.aggregate_id, stamp, (place, st) => {
    // `PlaceState.name` is `Register<string | null>` (Task 3), so a `null`
    // payload is a clear, not malformed input — `writeNullableIfPresent`'s rule.
    const next = writeNullableIfPresent(
      place.name,
      readString(op.payload, 'name'),
      st,
    )
    // `writeRegister` (inside `writeNullableIfPresent`) returns the identical
    // register on a lost write or an absent field; a spread here would still
    // fabricate a new `place` object, so the identity must be checked before
    // copying, not after. `next` is only ever `undefined` when it equals
    // `place.name` (both absent) — that branch is caught above, so the
    // second check is for the type checker, not runtime, under
    // `exactOptionalPropertyTypes`.
    if (next === place.name) return place
    return next === undefined ? place : { ...place, name: next }
  })

/**
 * `gear.recorded` (`sync-protocol.md` §4.3): creates the Gear and seeds each
 * **present** field as its own register, all stamped with this op's clock.
 * An absent field leaves its register absent — it is not defaulted. A second
 * `gear.recorded` for the same id (there is no other op for `container`) is
 * just an ordinary LWW write on each register it carries; nothing here
 * special-cases a repeat.
 */
const gearRecorded: Handler = (state, op, stamp) =>
  writeGear(state, op.aggregate_id, stamp, (gear, st) => {
    const name = writeNullableIfPresent(
      gear.name,
      readString(op.payload, 'name'),
      st,
    )
    const container = writeIfPresent(
      gear.container,
      readBoolean(op.payload, 'container'),
      st,
    )
    // An unrecognised `kind` is stored verbatim (§5.3 obligation 4) — hence
    // `readOpen`, not a closed enum reader.
    const kind = writeIfPresent(gear.kind, readOpen(op.payload, 'kind'), st)
    const residence = writeIfPresent(
      gear.residence,
      readResidence(op.payload, 'residence'),
      st,
    )
    const owner = writeIfPresent(gear.owner, readOwner(op.payload, 'owner'), st)
    const ownedCount = writeIfPresent(
      gear.ownedCount,
      readCount(op.payload, 'owned_count'),
      st,
    )
    if (
      name === gear.name &&
      container === gear.container &&
      kind === gear.kind &&
      residence === gear.residence &&
      owner === gear.owner &&
      ownedCount === gear.ownedCount
    ) {
      return gear
    }
    return {
      ...gear,
      ...(name === undefined ? {} : { name }),
      ...(container === undefined ? {} : { container }),
      ...(kind === undefined ? {} : { kind }),
      ...(residence === undefined ? {} : { residence }),
      ...(owner === undefined ? {} : { owner }),
      ...(ownedCount === undefined ? {} : { ownedCount }),
    }
  })

/** `gear.renamed` (§4.3): sets `name`, via `writeNullableIfPresent`'s rule. */
const gearRenamed: Handler = (state, op, stamp) =>
  writeGear(state, op.aggregate_id, stamp, (gear, st) => {
    const next = writeNullableIfPresent(
      gear.name,
      readString(op.payload, 'name'),
      st,
    )
    if (next === gear.name) return gear
    return next === undefined ? gear : { ...gear, name: next }
  })

/** `gear.rehomed` (§4.3): sets the **home** residence. A trip never touches it. */
const gearRehomed: Handler = (state, op, stamp) =>
  writeGear(state, op.aggregate_id, stamp, (gear, st) => {
    const residence = readResidence(op.payload, 'residence')
    if (residence.kind !== 'value') return gear
    const next = writeRegister(gear.residence, residence.value, st)
    return next === gear.residence ? gear : { ...gear, residence: next }
  })

/**
 * `gear.kind_set` (§4.3): sets `kind`. Exclusivity is structural — one
 * register, one value (domain invariant 5) — so there is nothing to guard.
 * An unrecognised value is stored verbatim (§5.3 obligation 4).
 */
const gearKindSet: Handler = (state, op, stamp) =>
  writeGear(state, op.aggregate_id, stamp, (gear, st) => {
    const kind = readOpen(op.payload, 'kind')
    if (kind.kind !== 'value') return gear
    const next = writeRegister(gear.kind, kind.value, st)
    return next === gear.kind ? gear : { ...gear, kind: next }
  })

/**
 * `gear.owned_count_set` (§4.3): sets `ownedCount` **absolutely**, never a
 * delta — a counter would be hazardous under replay and under two devices
 * closing the same trip. The payload key is `count`, not `owned_count`.
 */
const gearOwnedCountSet: Handler = (state, op, stamp) =>
  writeGear(state, op.aggregate_id, stamp, (gear, st) => {
    const count = readCount(op.payload, 'count')
    if (count.kind !== 'value') return gear
    const next = writeRegister(gear.ownedCount, count.value, st)
    return next === gear.ownedCount ? gear : { ...gear, ownedCount: next }
  })

/**
 * `gear.tag_applied` / `gear.tag_removed` (`sync-protocol.md` §4.3), which
 * are the same handler with a different value — an ordinary LWW pair on one
 * **per-tag** register (§3.4).
 *
 * The whole concurrency story is the register key, not the code here. Two
 * devices applying *different* tags address different registers, so neither
 * write is contested and both survive: the union is not computed, it is the
 * absence of a conflict. Apply and remove of the *same* tag address one
 * register and resolve by `writeRegister` like every other field.
 *
 * `readString`, deliberately, and no normalisation: §5's tolerant reader
 * outranks §4.3's `TagString` rule, so the key is the literal string that
 * arrived. `tags.ts` applies the rule on the way out, which is the only place
 * a spelling is ever decided.
 */
const gearTagWritten =
  (present: boolean): Handler =>
  (state, op, stamp) =>
    writeGear(state, op.aggregate_id, stamp, (gear, st) => {
      const tag = readString(op.payload, 'tag')
      if (tag.kind !== 'value') return gear
      const current = gear.tags?.[tag.value]
      const next = writeRegister(current, present, st)
      // Identity propagated, same as every other handler: a lost write must
      // not fabricate a new `gear` and invalidate a memo downstream.
      if (next === current) return gear
      return { ...gear, tags: { ...gear.tags, [tag.value]: next } }
    })

/**
 * `person.recorded` and `person.renamed` both just set `name`
 * (`sync-protocol.md` §4.2) — the entity itself is created by `writePerson`
 * either way, out of authoring order or not (§8.2). The same shape
 * `setPlaceName` above already has, for the same reason.
 *
 * **`person.renamed` joined this handler at S4**, and settled §4.2's deferred
 * question by doing so: it was left as `{name}` "until the slice that folds it
 * settles the same question for that row", and the answer is that
 * `PersonState.name` is `Register<string | null>`, so
 * `writeNullableIfPresent`'s rule applies unchanged and an explicit `null`
 * clears. There was never a second rule to find — only a register whose type
 * had already decided.
 */
const setPersonName: Handler = (state, op, stamp) =>
  writePerson(state, op.aggregate_id, stamp, (person, st) => {
    const next = writeNullableIfPresent(
      person.name,
      readString(op.payload, 'name'),
      st,
    )
    if (next === person.name) return person
    return next === undefined ? person : { ...person, name: next }
  })

/**
 * `gear.ownership_set` (§4.3): sets `owner`. One register, one value — the
 * domain's "personal to one person, **or** shared" is structural, so there is
 * nothing to guard and returning gear to the pool is a write, not a clear.
 *
 * An **absent** register is not the same fact as `{type:'shared'}`, and this
 * handler never conflates them: it writes only what arrived. That the two
 * *read* alike is `selectors/owner.ts`'s decision, made once, on the way out.
 */
const gearOwnershipSet: Handler = (state, op, stamp) =>
  writeGear(state, op.aggregate_id, stamp, (gear, st) => {
    const owner = readOwner(op.payload, 'owner')
    if (owner.kind !== 'value') return gear
    const next = writeRegister(gear.owner, owner.value, st)
    return next === gear.owner ? gear : { ...gear, owner: next }
  })

/**
 * `trip.created` (`sync-protocol.md` §4.4): creates the Trip, seeds `name`,
 * seeds `phase = "draft"`, and folds the optional template provenance.
 *
 * `gearRecorded`'s shape rather than `setPlaceName`'s, because this op writes
 * more than one register — and the second one is written by **this handler**,
 * not carried in the payload (spec §1.3). §4.4's payload is
 * `{name, from_trip_id?}`; the phase is stamped with this op's own clock, and
 * three properties follow from that with no special case anywhere:
 *
 * - A `trip.phase_moved` delivered **before** its creation wins, because its
 *   clock is strictly later. §8.2's out-of-authoring-order case, resolved by
 *   the ordinary rule.
 * - A re-delivered `trip.created` (our own op returning through pull, §8.3)
 *   writes identical values on an identical stamp and loses on `<= 0`.
 *   Idempotent for free.
 * - No payload can carry a phase, so no client can create a Trip already
 *   `closed`. An absence, not a guard.
 *
 * `from_trip_id` is folded and read by nobody until S14 (spec §1.3). §5.4
 * freezes this payload the moment S6 ships, so a field the reducer dropped
 * would be a field no fixture could prove was carried — and `authoring.ts`
 * has no parameter for it, so the only writer is a hand-shaped op.
 */
const tripCreated: Handler = (state, op, stamp) =>
  writeTrip(state, op.aggregate_id, stamp, (trip, st) => {
    // `TripState.name` is `Register<string | null>` like every other name
    // register, so an explicit `null` is a clear (§1.3, `setPlaceName`'s rule).
    const name = writeNullableIfPresent(
      trip.name,
      readString(op.payload, 'name'),
      st,
    )
    const phase = writeRegister<PhaseValue>(trip.phase, 'draft', st)
    const fromTripId = writeIfPresent(
      trip.fromTripId,
      readString(op.payload, 'from_trip_id'),
      st,
    )
    // Identity across all three before the spread, not after: a wholly lost
    // write must return the identical `TripState` so `writeTrip` can return
    // the identical `HouseholdState`. `phase` is never `undefined` here —
    // `writeRegister` always returns a register — so only the two optional
    // reads need the `undefined` guard on the way out.
    if (
      name === trip.name &&
      phase === trip.phase &&
      fromTripId === trip.fromTripId
    ) {
      return trip
    }
    return {
      ...trip,
      ...(name === undefined ? {} : { name }),
      phase,
      ...(fromTripId === undefined ? {} : { fromTripId }),
    }
  })

/**
 * `trip.renamed` (`sync-protocol.md` §4.4): sets `name`, via
 * `writeNullableIfPresent`'s rule — the eighth `name` row, settled by this
 * slice from the general rule and not a new one (spec §1.2).
 *
 * Unlike `setPlaceName` and `setPersonName` this sits under **one** key: its
 * partner `trip.created` writes two further registers, so it needs
 * `gearRecorded`'s shape and an identity check spanning all three. The three
 * lines of name write are duplicated there rather than extracted here,
 * exactly as `gearRecorded` and `gearRenamed` already duplicate them.
 */
const setTripName: Handler = (state, op, stamp) =>
  writeTrip(state, op.aggregate_id, stamp, (trip, st) => {
    const next = writeNullableIfPresent(
      trip.name,
      readString(op.payload, 'name'),
      st,
    )
    if (next === trip.name) return trip
    return next === undefined ? trip : { ...trip, name: next }
  })

/**
 * `trip.dates_set` (`sync-protocol.md` §4.4): two **independent** registers,
 * each following the absent-versus-null rule separately (§1.3), so
 * `{start}` alone leaves the end exactly as it was and `{end: null}` clears
 * the end and only the end. That is what lets the screen emit just what
 * changed instead of re-asserting both dates on every save.
 *
 * The payload keys are `start`/`end`; the registers are `startDate`/
 * `endDate` (spec §1.4) — the split `gear.owned_count_set{count}` already
 * has, named here so nobody "fixes" it later.
 *
 * `readString`, with **no `YYYY-MM-DD` gate**: a reader that reported
 * anything else `absent` would be rejecting a quartermaster's work to enforce
 * a spelling, which is what §5.3 forbids and §4.3's `TagString` note argues
 * at length. A malformed date is stored verbatim and drawn verbatim — visible
 * rather than silently dropped — and the display sort stays lexicographic,
 * which is exactly right for `YYYY-MM-DD` and total for anything else. There
 * is likewise no end-before-start guard: the domain states no such invariant,
 * and two devices may legitimately write the two registers concurrently, so a
 * guard would have to discard one of two valid writes.
 */
const tripDatesSet: Handler = (state, op, stamp) =>
  writeTrip(state, op.aggregate_id, stamp, (trip, st) => {
    const startDate = writeNullableIfPresent(
      trip.startDate,
      readString(op.payload, 'start'),
      st,
    )
    const endDate = writeNullableIfPresent(
      trip.endDate,
      readString(op.payload, 'end'),
      st,
    )
    if (startDate === trip.startDate && endDate === trip.endDate) return trip
    return {
      ...trip,
      ...(startDate === undefined ? {} : { startDate }),
      ...(endDate === undefined ? {} : { endDate }),
    }
  })

/**
 * `trip.phase_moved` (`sync-protocol.md` §4.4): sets `phase`. `gearKindSet`'s
 * shape exactly — one register, one value, so exclusivity is structural and
 * there is nothing to guard, and invariant 16 makes every move expressible in
 * either direction, so there is no transition graph to enforce either.
 *
 * `readOpen`, not a closed enum reader: an unrecognised phase is stored
 * verbatim (§5.3 obligation 4) and `selectors/trip.ts` decides what the app
 * does with one — not active, filed under `PLANNED`, no next step.
 */
const tripPhaseMoved: Handler = (state, op, stamp) =>
  writeTrip(state, op.aggregate_id, stamp, (trip, st) => {
    const phase = readOpen(op.payload, 'phase')
    if (phase.kind !== 'value') return trip
    const next = writeRegister<PhaseValue>(trip.phase, phase.value, st)
    return next === trip.phase ? trip : { ...trip, phase: next }
  })

/**
 * `trip.participant_added` / `trip.participant_removed`
 * (`sync-protocol.md` §4.4): `gearTagWritten`'s handler with `person_id` for
 * `tag` and `participants` for `tags` — an ordinary LWW pair on one
 * **per-person** register (§3.4).
 *
 * The concurrency story is the register key, not the code. Two devices adding
 * *different* People address different registers, so neither write is
 * contested and both survive: the union is not computed, it is the absence of
 * a conflict. An add racing a remove of the *same* Person is one register
 * resolving by `writeRegister` like every other field.
 *
 * `false` is a real value carrying a real clock, never a dropped key —
 * dropping it would let a concurrent re-add win by arrival order.
 */
const tripParticipantWritten =
  (present: boolean): Handler =>
  (state, op, stamp) =>
    writeTrip(state, op.aggregate_id, stamp, (trip, st) => {
      const personId = readString(op.payload, 'person_id')
      if (personId.kind !== 'value') return trip
      const current = trip.participants?.[personId.value]
      const next = writeRegister(current, present, st)
      // Identity propagated, same as every other handler: a lost write must
      // not fabricate a new `trip` and invalidate a memo downstream.
      if (next === current) return trip
      return {
        ...trip,
        participants: { ...trip.participants, [personId.value]: next },
      }
    })

/**
 * `trip.consumption_posted` (S11, spec §2.1, §4): writes
 * `trips.<id>.postings.<gear_id>`, `tripParticipantWritten`'s exact shape one
 * field over — a per-gear register on the Trip root, resolved by plain LWW.
 *
 * **Folds unconditionally.** The Kind lives on the Gear aggregate, which has
 * no ordering against the Trip's, so gating here on Kind or on whether
 * `state.gear[gear_id]` has arrived would make the fold depend on which op
 * happened to land first — `bringCountOf`'s and `consumedCountOf`'s reason,
 * restated for a third register. Every gate belongs to `postedOf`, on the way
 * out.
 *
 * **`units` is absolute, never a delta** — `gear.owned_count_set`'s own
 * contract, one register over. `readCount` accepts `0`, and this reducer
 * writes it exactly as it writes any other value: a restoration back to
 * nothing is a fact distinct from the register never having been addressed.
 */
const tripConsumptionPosted: Handler = (state, op, stamp) => {
  const gearId = readString(op.payload, 'gear_id')
  if (gearId.kind !== 'value') return state
  const units = readCount(op.payload, 'units')
  if (units.kind !== 'value') return state
  return writeTrip(state, op.aggregate_id, stamp, (trip, st) => {
    const current = trip.postings?.[gearId.value]
    const next = writeRegister(current, units.value, st)
    // Identity propagated, same as every other handler: a lost write must
    // not fabricate a new `trip` and invalidate a memo downstream.
    if (next === current) return trip
    return {
      ...trip,
      postings: { ...trip.postings, [gearId.value]: next },
    }
  })
}

/**
 * `trip.note_posted` (§4.4): creates the Note and seeds `text`, optionally
 * `entry_id` (S12 spec §2).
 *
 * Both fields go through `writeIfPresent`, which for `entry_id` is the whole
 * of the not-nullable decision: an explicit `null` reads as `null`, matches no
 * branch, and leaves the register standing. See {@link NoteState} for why that
 * is the right answer rather than a missing case.
 *
 * A malformed `text` leaves a Note with no text — the same shape a review
 * arriving before its post leaves — which `notesOf` excludes from every list
 * and every count. Retained in the fold, drawn nowhere, exactly as
 * `trip.entry_added`'s sourceless Entry is.
 */
const tripNotePosted: Handler = (state, op, stamp) => {
  const noteId = readString(op.payload, 'note_id')
  if (noteId.kind !== 'value') return state
  return writeNote(state, op.aggregate_id, noteId.value, stamp, (note, st) => {
    const text = writeIfPresent(note.text, readString(op.payload, 'text'), st)
    const entryId = writeIfPresent(
      note.entryId,
      readString(op.payload, 'entry_id'),
      st,
    )
    if (text === note.text && entryId === note.entryId) return note
    return {
      ...note,
      ...(text !== undefined && { text }),
      ...(entryId !== undefined && { entryId }),
    }
  })
}

/**
 * `trip.note_kept` (§4.4): `true` keeps as reference, `false` discards.
 *
 * One op in both directions, so a discard is reversed by an ordinary later
 * keep and nothing is special-cased (ruling I14). Folded on **any** Note,
 * including one this replica has not seen posted: the review and the post
 * address different registers on one entity path, and neither waits for the
 * other.
 */
const tripNoteKept: Handler = (state, op, stamp) => {
  const noteId = readString(op.payload, 'note_id')
  if (noteId.kind !== 'value') return state
  const kept = readBoolean(op.payload, 'kept')
  if (kept.kind !== 'value') return state
  return writeNote(state, op.aggregate_id, noteId.value, stamp, (note, st) => {
    const next = writeRegister(note.kept, kept.value, st)
    if (next === note.kept) return note
    return { ...note, kept: next }
  })
}

/**
 * `trip.entry_added` (§4.4): creates the Entry and seeds `source`.
 *
 * A malformed or unrecognised source writes nothing and leaves an Entry that
 * `entriesOf` excludes — retained in the fold, drawn nowhere, holding no
 * claim. There is no defaultable value for `source`, so unlike `phase` it
 * gets no fallback.
 */
const tripEntryAdded: Handler = (state, op, stamp) => {
  const entryId = readString(op.payload, 'entry_id')
  if (entryId.kind !== 'value') return state
  return writeEntry(
    state,
    op.aggregate_id,
    entryId.value,
    stamp,
    (entry, st) => {
      const source = writeIfPresent(
        entry.source,
        readSource(op.payload, 'source'),
        st,
      )
      if (source === entry.source) return entry
      return { ...entry, ...(source === undefined ? {} : { source }) }
    },
  )
}

/**
 * `trip.entry_removed` (§4.4): the tombstone, and the only way an over-claim
 * is resolved (§3.6). An ordinary LWW field — delete does not win by being a
 * delete. No restore op exists in the MVP; re-adding is a new Entry with a
 * new id.
 */
const tripEntryRemoved: Handler = (state, op, stamp) => {
  const entryId = readString(op.payload, 'entry_id')
  if (entryId.kind !== 'value') return state
  return writeEntry(
    state,
    op.aggregate_id,
    entryId.value,
    stamp,
    (entry, st) => {
      const next = writeRegister(entry.removed, true, st)
      return next === entry.removed ? entry : { ...entry, removed: next }
    },
  )
}

/**
 * `trip.entry_bring_count_set` (§4.4): sets `bringCount` absolutely.
 *
 * The catalogue's "Counted entries only" is an **authoring** rule, not a
 * reader gate: the Entry's Kind lives on the Gear aggregate, and resolving it
 * here would make the fold order-dependent on whether `gear.kind_set` had
 * arrived. Readers gate through `bringCountOf` instead.
 */
const tripEntryBringCountSet: Handler = (state, op, stamp) => {
  const entryId = readString(op.payload, 'entry_id')
  if (entryId.kind !== 'value') return state
  const count = readCount(op.payload, 'count')
  if (count.kind !== 'value') return state
  return writeEntry(
    state,
    op.aggregate_id,
    entryId.value,
    stamp,
    (entry, st) => {
      const next = writeRegister(entry.bringCount, count.value, st)
      return next === entry.bringCount ? entry : { ...entry, bringCount: next }
    },
  )
}

/**
 * `trip.piece_removed` / `trip.piece_restored` (`sync-protocol.md` §4.4): an
 * ordinary LWW pair on one register (§3.5). Delete does not win by being a
 * delete; a restore wins only by being strictly later.
 *
 * `false` is a real value carrying a real clock, never a dropped key — the
 * rule `participants` and `tags` already keep.
 */
const tripPieceWritten =
  (removed: boolean): Handler =>
  (state, op, stamp) => {
    const entryId = readString(op.payload, 'entry_id')
    if (entryId.kind !== 'value') return state
    const personId = readString(op.payload, 'person_id')
    if (personId.kind !== 'value') return state
    return writePiece(
      state,
      op.aggregate_id,
      entryId.value,
      personId.value,
      stamp,
      (piece, st) => {
        const next = writeRegister(piece.removed, removed, st)
        return next === piece.removed ? piece : { ...piece, removed: next }
      },
    )
  }

/**
 * `trip.entry_status_set` / `trip.container_stage_set` (`sync-protocol.md`
 * §4.4). Two ops, one shape: read `entry_id`, write one open-enum register on
 * the Entry by plain LWW (§3.3).
 *
 * **Neither is gated on the Gear's containment trait, and neither may be.**
 * The trait lives on another aggregate with no ordering against the Trip's, so
 * a gate here would make the fold order-dependent on whether `gear.recorded`
 * had arrived. §3.7's *never both on one entry* is an authoring rule and the
 * gate lives on the way out, in `selectors/packing.ts` — the same split
 * `bringCount` takes for invariant 6 and `tags` for `TagString`.
 *
 * One factory over `'status' | 'stage'`, `gearTagWritten` and
 * `tripParticipantWritten`'s shape: `entry[field]` is a union of two
 * `Register` types, but `writeRegister`'s own type parameter infers to their
 * common supertype (`string`, since both `StatusValue` and `StageValue` widen
 * to it) rather than forcing a cast, and the computed-key spread checks
 * against `EntryState` the same way. Confirmed against this file's own
 * `tsconfig` before relying on it — no `as`, no `any`, no `!`.
 */
const tripEntryOpenEnum =
  (field: 'status' | 'stage'): Handler =>
  (state, op, stamp) => {
    const entryId = readString(op.payload, 'entry_id')
    if (entryId.kind !== 'value') return state
    const value = readOpen(op.payload, field)
    if (value.kind !== 'value') return state
    return writeEntry(
      state,
      op.aggregate_id,
      entryId.value,
      stamp,
      (entry, st) => {
        const next = writeRegister(entry[field], value.value, st)
        return next === entry[field] ? entry : { ...entry, [field]: next }
      },
    )
  }

/** `trip.piece_status_set` (§4.4) — the Entry's `status`, one path deeper. */
const tripPieceStatusSet: Handler = (state, op, stamp) => {
  const entryId = readString(op.payload, 'entry_id')
  if (entryId.kind !== 'value') return state
  const personId = readString(op.payload, 'person_id')
  if (personId.kind !== 'value') return state
  const status = readOpen(op.payload, 'status')
  if (status.kind !== 'value') return state
  return writePiece(
    state,
    op.aggregate_id,
    entryId.value,
    personId.value,
    stamp,
    (piece, st) => {
      const next = writeRegister(piece.status, status.value, st)
      return next === piece.status ? piece : { ...piece, status: next }
    },
  )
}

/**
 * `trip.outcome_set` (`sync-protocol.md` §4.4): the unpack outcome, on the
 * Entry or — when `person_id` is present — on one Piece.
 *
 * **The first op whose entity path a payload field chooses.** `person_id`
 * present → {@link writePiece}; absent → {@link writeEntry}. A `person_id`
 * that is present but unreadable reads `absent` through `readString`, so the
 * outcome lands on the **Entry** — the conservative direction: the tolerant
 * reader's own answer is that a field it cannot read was not there (§1.3),
 * and it puts the outcome on the line the Quartermaster was looking at
 * rather than dropping it.
 *
 * `writeNullableIfPresent`, not `writeIfPresent`: the register's declared
 * type includes `null`, so an explicit `null` **clears it back to open** and
 * an absent field leaves it alone (§1.3, not §5.3 obligation 5).
 *
 * `readOpen(op.payload, 'outcome')` returns `Read<string>`; the Entry's and
 * the Piece's `outcome` registers are both `Register<OutcomeValue | null>`.
 * Two calls, one per branch, rather than one shared helper carrying an
 * explicit `<OutcomeValue>` — `writeNullableIfPresent`'s own type parameter
 * infers to the common supertype at each call site, exactly as
 * `tripEntryOpenEnum`'s `writeRegister` call does one register over. No `as`,
 * no `any`, no `!`.
 *
 * The two-step identity guard (`next === current`, then `next === undefined`)
 * is `setPlaceName`'s and `gearRenamed`'s, copied rather than the naive
 * one-line ternary a nullable write cannot use: `writeNullableIfPresent`'s
 * own declared return type is `Register<T | null> | undefined` — it can hand
 * back `undefined` when its `read` argument is absent — and spreading
 * `{ …entry, outcome: next }` with a value of that type makes `outcome` a
 * **required** property typed `T | undefined`, a different shape from the
 * optional `outcome?: T` the interface declares; `exactOptionalPropertyTypes`
 * rejects the two as different types on the *declared* type alone. In this
 * handler `next` can in fact never be `undefined` at runtime — the
 * `outcome.kind === 'absent'` guard above already returned, so
 * `writeNullableIfPresent` always reaches `writeRegister`, which never
 * returns `undefined` — but the type checker cannot see across that earlier
 * guard into this callback. The first check (`next === current`) is the real
 * one; the second is purely for the type checker.
 */
const tripOutcomeSet: Handler = (state, op, stamp) => {
  const entryId = readString(op.payload, 'entry_id')
  if (entryId.kind !== 'value') return state
  const outcome = readOpen(op.payload, 'outcome')
  if (outcome.kind === 'absent') return state
  const personId = readString(op.payload, 'person_id')

  if (personId.kind === 'value') {
    return writePiece(
      state,
      op.aggregate_id,
      entryId.value,
      personId.value,
      stamp,
      (piece, st) => {
        const next = writeNullableIfPresent(piece.outcome, outcome, st)
        if (next === piece.outcome) return piece
        return next === undefined ? piece : { ...piece, outcome: next }
      },
    )
  }
  return writeEntry(
    state,
    op.aggregate_id,
    entryId.value,
    stamp,
    (entry, st) => {
      const next = writeNullableIfPresent(entry.outcome, outcome, st)
      if (next === entry.outcome) return entry
      return next === undefined ? entry : { ...entry, outcome: next }
    },
  )
}

/**
 * `trip.consumed_count_set` (§4.4): sets `consumedCount` absolutely.
 *
 * The catalogue's *"on a counted Entry resolved as `consumed`"* is an
 * **authoring** rule on both halves — the Kind lives on the Gear aggregate
 * and the outcome is a second register on the same Entry — so this folds on
 * any Entry unconditionally, and `consumedCountOf` gates on the way out,
 * exactly as `bringCountOf` does one register over (§1.4).
 */
const tripConsumedCountSet: Handler = (state, op, stamp) => {
  const entryId = readString(op.payload, 'entry_id')
  if (entryId.kind !== 'value') return state
  const count = readCount(op.payload, 'count')
  if (count.kind !== 'value') return state
  return writeEntry(
    state,
    op.aggregate_id,
    entryId.value,
    stamp,
    (entry, st) => {
      const next = writeRegister(entry.consumedCount, count.value, st)
      return next === entry.consumedCount
        ? entry
        : { ...entry, consumedCount: next }
    },
  )
}

/**
 * `trip.entry_moved` (§4.4): the Entry's **trip** residence.
 *
 * Never its home (invariant 13) and never its status (invariant 12) — two
 * registers the merge can never make agree, which is what makes the duffel in
 * the car with an unpacked stove inside it a *reportable* disagreement rather
 * than a forbidden state.
 *
 * Moving a container writes **this one register on the container** and nothing
 * else: containment is a pointer held by the contained thing, so the contents
 * follow with no fan-out (story 10, spec §1.5).
 */
const tripEntryMoved: Handler = (state, op, stamp) => {
  const entryId = readString(op.payload, 'entry_id')
  if (entryId.kind !== 'value') return state
  const residence = readTripResidence(op.payload, 'residence')
  if (residence.kind !== 'value') return state
  return writeEntry(
    state,
    op.aggregate_id,
    entryId.value,
    stamp,
    (entry, st) => {
      const next = writeRegister(entry.residence, residence.value, st)
      return next === entry.residence ? entry : { ...entry, residence: next }
    },
  )
}

/** `trip.piece_moved` (§4.4) — {@link tripEntryMoved}, one path deeper. */
const tripPieceMoved: Handler = (state, op, stamp) => {
  const entryId = readString(op.payload, 'entry_id')
  if (entryId.kind !== 'value') return state
  const personId = readString(op.payload, 'person_id')
  if (personId.kind !== 'value') return state
  const residence = readTripResidence(op.payload, 'residence')
  if (residence.kind !== 'value') return state
  return writePiece(
    state,
    op.aggregate_id,
    entryId.value,
    personId.value,
    stamp,
    (piece, st) => {
      const next = writeRegister(piece.residence, residence.value, st)
      return next === piece.residence ? piece : { ...piece, residence: next }
    },
  )
}

/**
 * `trip.task_added` (§4.4): creates the Pre-trip task and seeds its `text`.
 *
 * `ticked` is deliberately not written — a new Task is unticked **by
 * absence**, which is `taskTickedOf`'s rule and not a payload field. That is
 * what makes a re-delivered add idempotent and lets a `trip.task_ticked` that
 * arrived first survive the add landing after it: nothing here contests the
 * register that op wrote.
 *
 * A payload with no `text` writes nothing at all, so no Task is created. There
 * is no defaultable value for a checklist line, exactly as there is none for
 * an Entry's `source`.
 */
const tripTaskAdded: Handler = (state, op, stamp) => {
  const taskId = readString(op.payload, 'task_id')
  if (taskId.kind !== 'value') return state
  return writeTask(state, op.aggregate_id, taskId.value, stamp, (task, st) => {
    const text = writeIfPresent(task.text, readString(op.payload, 'text'), st)
    if (text === task.text) return task
    return { ...task, ...(text === undefined ? {} : { text }) }
  })
}

/**
 * `trip.task_ticked` (§4.4): sets `ticked`. **One op for both directions** —
 * `true` and `false` are two values of one register, not a create and a
 * delete. That is `tripParticipantWritten`'s and `tripPieceWritten`'s shape
 * without even needing their curried pair, because here the value rides the
 * payload rather than the op type.
 *
 * **Folds unconditionally**, and may create a Task holding `ticked` and no
 * `text`: a peer may tick on another Device while the add is still queued.
 * Nothing about a Task lives on another aggregate, so this is not
 * `bringCount`'s cross-aggregate case — the reason here is plain arrival
 * order, and the reader is where a textless Task is dropped.
 */
const tripTaskTicked: Handler = (state, op, stamp) => {
  const taskId = readString(op.payload, 'task_id')
  if (taskId.kind !== 'value') return state
  const ticked = readBoolean(op.payload, 'ticked')
  if (ticked.kind !== 'value') return state
  return writeTask(state, op.aggregate_id, taskId.value, stamp, (task, st) => {
    const next = writeRegister(task.ticked, ticked.value, st)
    if (next === task.ticked) return task
    return { ...task, ticked: next }
  })
}

/**
 * The op-type dispatch table (`sync-protocol.md` §4.1, §4.3). A `Record`, not
 * a `switch`, so "is this type known?" is a lookup — the same question the
 * tolerant reader asks.
 */
const handlers: Record<string, Handler> = {
  'place.recorded': setPlaceName,
  'place.renamed': setPlaceName,
  // A tombstone is an ordinary LWW field; an edit never writes it (§3.5).
  'place.removed': (state, op, stamp) =>
    writePlace(state, op.aggregate_id, stamp, (place, st) => {
      const next = writeRegister(place.removed, true, st)
      return next === place.removed ? place : { ...place, removed: next }
    }),
  'gear.recorded': gearRecorded,
  'gear.renamed': gearRenamed,
  'gear.rehomed': gearRehomed,
  'gear.kind_set': gearKindSet,
  'gear.owned_count_set': gearOwnedCountSet,
  'gear.ownership_set': gearOwnershipSet,
  // Per-tag registers (§3.4). Present and absent, not create and delete.
  'gear.tag_applied': gearTagWritten(true),
  'gear.tag_removed': gearTagWritten(false),
  // `gear.retired` / `gear.restored` are an ordinary LWW pair on `retired`
  // (§3.5); an edit never writes it, so a retire that a later edit races
  // still leaves the gear retired.
  'gear.retired': (state, op, stamp) =>
    writeGear(state, op.aggregate_id, stamp, (gear, st) => {
      const next = writeRegister(gear.retired, true, st)
      return next === gear.retired ? gear : { ...gear, retired: next }
    }),
  'gear.restored': (state, op, stamp) =>
    writeGear(state, op.aggregate_id, stamp, (gear, st) => {
      const next = writeRegister(gear.retired, false, st)
      return next === gear.retired ? gear : { ...gear, retired: next }
    }),
  // Both only set `name`, so one handler serves both — the same pairing
  // `place.recorded`/`place.renamed` already has above.
  'person.recorded': setPersonName,
  'person.renamed': setPersonName,
  // The fourth aggregate (§4.4). `trip.created` is not paired with
  // `trip.renamed` under one handler the way the other two `recorded`/
  // `renamed` pairs are, because it writes two further registers of its own.
  'trip.created': tripCreated,
  'trip.renamed': setTripName,
  'trip.dates_set': tripDatesSet,
  'trip.phase_moved': tripPhaseMoved,
  // S14 (§4.4): the Trip's tombstone — `gear.retired`'s handler transplanted,
  // and deliberately **unpaired**, since the catalogue holds no
  // `trip.restored`. Inline for the same reason that pair is: the whole
  // handler is one register write, and a named `const` above would buy a name
  // for three lines nothing else calls.
  //
  // `writeTrip` creates the Trip for this op as it does for any other, so a
  // `trip.deleted` arriving before its `trip.created` still tombstones and the
  // creation still lands its name — the two registers are independent and
  // neither can lose the other's write.
  'trip.deleted': (state, op, stamp) =>
    writeTrip(state, op.aggregate_id, stamp, (trip, st) => {
      const next = writeRegister(trip.deleted, true, st)
      return next === trip.deleted ? trip : { ...trip, deleted: next }
    }),
  // Per-person registers (§3.4). Present and absent, not create and delete —
  // exactly the tag pair above.
  'trip.participant_added': tripParticipantWritten(true),
  'trip.participant_removed': tripParticipantWritten(false),
  // S7 (§4.4): the gear list, keyed by entry id — the first of the Trip's
  // nested maps.
  'trip.entry_added': tripEntryAdded,
  'trip.entry_removed': tripEntryRemoved,
  'trip.entry_bring_count_set': tripEntryBringCountSet,
  // S8 (§4.4): one Piece per Participant, keyed by person id on the Entry —
  // present and absent, not create and delete, exactly the participant and
  // tag pairs above.
  'trip.piece_removed': tripPieceWritten(true),
  'trip.piece_restored': tripPieceWritten(false),
  // S9a (§4.4): the two tracks — *where* and *how far along* — as five
  // registers over two entity paths. Separate registers is what makes
  // invariant 12 structural rather than enforced: no merge can make them
  // agree, so the disagreement survives to be reported.
  'trip.entry_status_set': tripEntryOpenEnum('status'),
  'trip.container_stage_set': tripEntryOpenEnum('stage'),
  'trip.piece_status_set': tripPieceStatusSet,
  'trip.entry_moved': tripEntryMoved,
  'trip.piece_moved': tripPieceMoved,
  // S10 (§4.4): `outcome` (Entry and Piece) and `consumedCount` (Entry) —
  // the last two registers `sync-protocol.md` §3.7 names for either path.
  'trip.outcome_set': tripOutcomeSet,
  'trip.consumed_count_set': tripConsumedCountSet,
  // S11 (§4.4): the posting register, `participants`' per-key shape one row
  // over on the Trip root — the fact that a close's reduction "applied once"
  // (domain §6), now with somewhere to be recorded.
  'trip.consumption_posted': tripConsumptionPosted,
  // S13 (§4.4): `tasks.<task_id>`, a nested entity map on the Trip —
  // `entries`' shape, not `participants`', because two registers on one
  // entity is not something a presence flag can hold. `task_ticked` needs no
  // add/remove pair: the value rides the payload, so one op writes both
  // directions.
  'trip.task_added': tripTaskAdded,
  'trip.task_ticked': tripTaskTicked,
  // S12 (§4.4): `notes.<note_id>`, the second of the Trip's nested entity
  // maps — `entries`' shape, not `participants`'. S13's `tasks` is the third
  // and shares nothing with it, which is the disjointness architecture §8.6
  // rests the parallel build on.
  'trip.note_posted': tripNotePosted,
  'trip.note_kept': tripNoteKept,
}

/**
 * Applies one op to `state` and returns the next state. Pure, with structural
 * sharing: only the touched entity, the map holding it, and the top-level
 * `HouseholdState` are copied. An unknown `type` is retained via `noteUnfolded`
 * rather than folded or rejected.
 */
export function applyOp(state: HouseholdState, op: OpEnvelope): HouseholdState {
  const handler = handlers[op.type]
  if (handler === undefined) return noteUnfolded(state, op.type)
  return handler(state, op, { hlc: op.hlc, deviceId: op.device_id })
}

/**
 * Folds a whole op log onto `from` (default: `emptyState()`). Order-
 * independent by construction (§3.2, §8.2): each register only ever accepts
 * a strictly later write, so folding the complete local log reproduces the
 * current state exactly (§8.4) — which is what makes a snapshot safely
 * discardable.
 */
export function fold(
  ops: Iterable<OpEnvelope>,
  from: HouseholdState = emptyState(),
): HouseholdState {
  let state = from
  for (const op of ops) {
    state = applyOp(state, op)
  }
  return state
}
